import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Package,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Truck,
  AlertCircle,
  Archive,
  ArchiveRestore,
  Trash2,
  AlertTriangle,
  RotateCcw,
  HelpCircle,
  Copy,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DELETE_CONFIRM_TEXT = 'DELETE';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [refundFilter, setRefundFilter] = useState('all'); // 'all' | 'requested' | 'refunded'
  const [view, setView] = useState('active'); // 'active' | 'archived'

  // Delete confirmation modal state
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Archive confirmation
  const [orderToArchive, setOrderToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  // Refund-mark modal
  const [orderToRefund, setOrderToRefund] = useState(null);
  const [refundForm, setRefundForm] = useState({ status: 'requested', amount: '', notes: '' });
  const [savingRefund, setSavingRefund] = useState(false);

  // Tracking modal
  const [orderToTrack, setOrderToTrack] = useState(null);
  const [trackingForm, setTrackingForm] = useState({ tracking_number: '', carrier: 'USPS', notify_customer: true });
  const [savingTracking, setSavingTracking] = useState(false);
  const [publicTrackingEnabled, setPublicTrackingEnabled] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [filterStatus, view, refundFilter]);

  useEffect(() => {
    // Load public flag state so tracking-link UI only shows when customers can use it
    axios
      .get(`${API_URL}/api/settings/public`)
      .then((r) => setPublicTrackingEnabled(!!r.data?.flags?.public_order_tracking))
      .catch(() => { /* non-fatal */ });
  }, []);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const fetchOrders = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      params.archived = view === 'archived' ? 'only' : 'false';
      if (refundFilter !== 'all') params.refund_status = refundFilter;
      const response = await axios.get(`${API_URL}/api/admin/orders/list`, {
        headers: authHeader(),
        params,
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/orders/stats/summary`, {
        headers: authHeader(),
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/orders/${orderId}/status?new_status=${newStatus}`,
        {},
        { headers: authHeader() }
      );
      toast.success('Order status updated');
      fetchOrders();
      fetchStats();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleArchive = async () => {
    if (!orderToArchive) return;
    setArchiving(true);
    try {
      const archived = orderToArchive.archived;
      const url = archived
        ? `${API_URL}/api/admin/orders/${orderToArchive.id}/unarchive`
        : `${API_URL}/api/admin/orders/${orderToArchive.id}/archive`;
      await axios.patch(url, {}, { headers: authHeader() });
      toast.success(archived ? 'Order restored' : 'Order archived');
      setOrderToArchive(null);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setArchiving(false);
    }
  };

  const openRefundModal = (order) => {
    setOrderToRefund(order);
    setRefundForm({
      status: order.refund_status === 'requested' ? 'requested' : 'requested',
      amount: order.refund_amount?.toString() || '',
      notes: order.refund_notes || '',
    });
  };

  const handleRefund = async () => {
    if (!orderToRefund) return;
    setSavingRefund(true);
    try {
      const body = {
        status: refundForm.status,
        amount: refundForm.amount ? parseFloat(refundForm.amount) : null,
        notes: refundForm.notes || null,
      };
      await axios.patch(
        `${API_URL}/api/admin/orders/${orderToRefund.id}/refund`,
        body,
        { headers: authHeader() }
      );
      toast.success('Refund status updated');
      setOrderToRefund(null);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update refund status');
    } finally {
      setSavingRefund(false);
    }
  };

  const openTrackingModal = (order) => {
    setOrderToTrack(order);
    setTrackingForm({
      tracking_number: order.tracking_number || '',
      carrier: order.carrier || 'USPS',
      notify_customer: !order.tracking_number, // default ON for first-time add
    });
  };

  const handleTrackingSave = async () => {
    if (!orderToTrack) return;
    if (trackingForm.notify_customer && !trackingForm.tracking_number.trim()) {
      toast.error('Add a tracking number before notifying the customer');
      return;
    }
    setSavingTracking(true);
    try {
      const body = {
        tracking_number: trackingForm.tracking_number,
        carrier: trackingForm.carrier,
        notify_customer: trackingForm.notify_customer,
      };
      const res = await axios.patch(
        `${API_URL}/api/admin/orders/${orderToTrack.id}/tracking`,
        body,
        { headers: authHeader() }
      );
      if (trackingForm.notify_customer) {
        toast.success(
          res.data?.email_status === 'sent'
            ? 'Tracking saved — shipped email sent to customer'
            : 'Tracking saved'
        );
      } else {
        toast.success('Tracking saved');
      }
      setOrderToTrack(null);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tracking');
    } finally {
      setSavingTracking(false);
    }
  };

  const copyTrackingLink = (order) => {
    if (!order.tracking_token) {
      toast.error('This order doesn\u2019t have a tracking token yet. Save tracking first.');
      return;
    }
    const url = `${window.location.origin}/track/${order.tracking_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Tracking link copied');
  };

  const getRefundBadge = (order) => {
    const status = order.refund_status;
    if (!status || status === 'none') return null;
    const config = {
      requested: { bg: 'bg-orange-500/15 border-orange-500/40 text-orange-300', label: 'Refund Requested' },
      refunded: { bg: 'bg-red-500/15 border-red-500/40 text-red-300', label: `Refunded${order.refund_amount ? ` $${order.refund_amount.toFixed(2)}` : ''}` },
      partially_refunded: { bg: 'bg-orange-500/15 border-orange-500/40 text-orange-300', label: `Partial Refund${order.refund_amount ? ` $${order.refund_amount.toFixed(2)}` : ''}` },
    }[status];
    if (!config) return null;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.bg}`} data-testid="refund-badge">
        <RotateCcw className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const handleDelete = async () => {
    if (!orderToDelete) return;
    if (deleteConfirmText !== DELETE_CONFIRM_TEXT) {
      toast.error(`Please type ${DELETE_CONFIRM_TEXT} to confirm`);
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/orders/${orderToDelete.id}`, {
        headers: authHeader(),
      });
      toast.success('Order permanently deleted');
      setOrderToDelete(null);
      setDeleteConfirmText('');
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      processing: { color: 'bg-yellow-500', icon: Clock, text: 'Processing' },
      confirmed: { color: 'bg-blue-500', icon: CheckCircle, text: 'Confirmed' },
      shipped: { color: 'bg-purple-500', icon: Truck, text: 'Shipped' },
      delivered: { color: 'bg-green-500', icon: CheckCircle, text: 'Delivered' },
      cancelled: { color: 'bg-red-500', icon: XCircle, text: 'Cancelled' },
    };
    const config = statusConfig[status] || statusConfig.processing;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const getPaymentBadge = (status) => {
    if (status === 'paid') return <Badge className="bg-green-500 text-white">Paid</Badge>;
    return <Badge className="bg-yellow-500 text-white">Pending</Badge>;
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Orders Management</h1>
        <p className="text-gray-400">
          {view === 'archived'
            ? 'Viewing archived orders. These are excluded from totals.'
            : 'View and manage all customer orders'}
        </p>
      </div>

      {/* Stats Cards (active orders only) */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Active Orders</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.total_orders}</p>
                  {stats.archived_orders > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{stats.archived_orders} archived</p>
                  )}
                </div>
                <Package className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Net Revenue</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    ${stats.total_revenue?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">excl. archived & refunds</p>
                </div>
                <DollarSign className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Processing</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats.orders_by_status?.processing || 0}
                  </p>
                </div>
                <Clock className="w-10 h-10 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Delivered</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats.orders_by_status?.delivered || 0}
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`bg-gray-800 ${(stats.refund_requested_count || 0) + (stats.refunded_count || 0) > 0 ? 'border-orange-500/40' : 'border-gray-700'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Refunds</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="refunds-count">
                    {(stats.refund_requested_count || 0) + (stats.refunded_count || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.refund_requested_count || 0} req · {stats.refunded_count || 0} done
                    {stats.total_refunded > 0 && ` · $${stats.total_refunded.toFixed(2)}`}
                  </p>
                </div>
                <RotateCcw className={`w-10 h-10 ${(stats.refund_requested_count || 0) + (stats.refunded_count || 0) > 0 ? 'text-orange-400' : 'text-gray-500'}`} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View toggle: Active vs Archived */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setView('active')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            view === 'active' ? 'bg-blue-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          data-testid="view-active-btn"
        >
          Active Orders
        </button>
        <button
          onClick={() => setView('archived')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            view === 'archived' ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          data-testid="view-archived-btn"
        >
          <Archive className="w-4 h-4" />
          Archived
          {stats?.archived_orders > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-gray-700 rounded-full text-xs">
              {stats.archived_orders}
            </span>
          )}
        </button>
      </div>

      {/* Status filter (only meaningful in active view) */}
      <div className="flex gap-2 mb-3 overflow-x-auto">
        {['all', 'processing', 'confirmed', 'shipped', 'delivered', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
              filterStatus === status
                ? 'bg-blue-500 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Refund filter */}
      <div className="flex gap-2 mb-6 overflow-x-auto items-center">
        <span className="text-xs text-gray-500 uppercase tracking-wide">Refunds:</span>
        {[
          { value: 'all', label: 'All' },
          { value: 'requested', label: 'Requested' },
          { value: 'refunded', label: 'Refunded' },
          { value: 'partially_refunded', label: 'Partial' },
        ].map((opt) => (
          <button
            key={opt.value}
            onClick={() => setRefundFilter(opt.value)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              refundFilter === opt.value
                ? 'bg-orange-500 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid={`refund-filter-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">
            {view === 'archived' ? 'Archived Orders' : 'Orders List'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-400 text-center py-8">Loading orders...</p>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                {view === 'archived' ? 'No archived orders' : 'No orders found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Order ID</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Customer</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Items</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Total</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Payment</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      className={`border-b border-gray-700 hover:bg-gray-750 ${
                        order.archived ? 'opacity-60' : ''
                      }`}
                      data-testid="order-row"
                    >
                      <td className="py-4 px-4">
                        <code className="text-blue-400 text-sm">{order.id}</code>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-white font-medium">{order.customer_name || 'Guest'}</p>
                          <p className="text-gray-400 text-sm">{order.customer_email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-white">{order.items?.length || 0}</td>
                      <td className="py-4 px-4">
                        <span className="text-white font-semibold">
                          ${order.total_amount?.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-4 px-4">{getPaymentBadge(order.payment_status)}</td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-1.5">
                          {getStatusBadge(order.order_status)}
                          {getRefundBadge(order)}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-400 text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-1 items-center">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="p-2 text-blue-400 hover:bg-blue-900/30 rounded"
                            title="View Details"
                            data-testid={`view-order-${order.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {!order.archived &&
                            order.order_status !== 'delivered' &&
                            order.order_status !== 'cancelled' && (
                              <select
                                value={order.order_status}
                                onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                className="bg-gray-700 text-white text-sm rounded px-2 py-1"
                              >
                                <option value="processing">Processing</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            )}

                          <button
                            onClick={() => openRefundModal(order)}
                            className="p-2 text-orange-400 hover:bg-orange-900/30 rounded"
                            title="Mark refund status"
                            data-testid={`refund-order-${order.id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => openTrackingModal(order)}
                            className={`p-2 rounded hover:bg-sky-900/30 ${
                              order.tracking_number ? 'text-sky-300' : 'text-sky-500/70'
                            }`}
                            title={order.tracking_number ? `Tracking: ${order.tracking_number}` : 'Add tracking'}
                            data-testid={`tracking-order-${order.id}`}
                          >
                            <Truck className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => setOrderToArchive(order)}
                            className="p-2 text-amber-400 hover:bg-amber-900/30 rounded"
                            title={order.archived ? 'Restore order' : 'Archive order'}
                            data-testid={`archive-order-${order.id}`}
                          >
                            {order.archived ? (
                              <ArchiveRestore className="w-4 h-4" />
                            ) : (
                              <Archive className="w-4 h-4" />
                            )}
                          </button>

                          <button
                            onClick={() => {
                              setOrderToDelete(order);
                              setDeleteConfirmText('');
                            }}
                            className="p-2 text-red-400 hover:bg-red-900/30 rounded"
                            title="Permanently delete"
                            data-testid={`delete-order-${order.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-800 border-gray-700 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Order Details</CardTitle>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Order ID</p>
                  <code className="text-blue-400">{selectedOrder.id}</code>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Date</p>
                  <p className="text-white">
                    {new Date(selectedOrder.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Customer Email</p>
                  <p className="text-white">{selectedOrder.customer_email}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Status</p>
                  {getStatusBadge(selectedOrder.order_status)}
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-3">Order Items</h3>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 bg-gray-700 p-3 rounded">
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-16 h-16 object-cover rounded"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.title}</p>
                        <p className="text-gray-400 text-sm">Quantity: {item.quantity}</p>
                      </div>
                      <p className="text-white font-semibold">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4 space-y-1 text-sm">
                {selectedOrder.subtotal !== undefined && (
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal:</span>
                    <span>${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                  </div>
                )}
                {selectedOrder.shipping_cost !== undefined && selectedOrder.shipping_cost > 0 && (
                  <div className="flex justify-between text-gray-400">
                    <span>Shipping{selectedOrder.shipping_method ? ` · ${selectedOrder.shipping_method}` : ''}:</span>
                    <span>${(selectedOrder.shipping_cost || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl pt-2">
                  <span className="text-gray-400">Total:</span>
                  <span className="text-white font-bold">
                    ${selectedOrder.total_amount?.toFixed(2)}
                  </span>
                </div>
              </div>

              {selectedOrder.refund_status && selectedOrder.refund_status !== 'none' && (
                <div>
                  <h3 className="text-white font-semibold mb-3">Refund Information</h3>
                  <div className="bg-gray-700 p-4 rounded space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span>{getRefundBadge(selectedOrder)}</span>
                    </div>
                    {selectedOrder.refund_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Amount:</span>
                        <span className="text-white">${selectedOrder.refund_amount.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.refunded_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Refunded at:</span>
                        <span className="text-white">{new Date(selectedOrder.refunded_at).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedOrder.refund_requested_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Requested at:</span>
                        <span className="text-white">{new Date(selectedOrder.refund_requested_at).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedOrder.refund_notes && (
                      <div>
                        <p className="text-gray-400 mb-1">Notes:</p>
                        <p className="text-white whitespace-pre-line">{selectedOrder.refund_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedOrder.payment_details && (
                <div>
                  <h3 className="text-white font-semibold mb-3">Payment Information</h3>
                  <div className="bg-gray-700 p-4 rounded space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Session ID:</span>
                      <code className="text-blue-400 text-sm">
                        {selectedOrder.payment_details.session_id}
                      </code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status:</span>
                      {getPaymentBadge(selectedOrder.payment_details.payment_status)}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Amount:</span>
                      <span className="text-white">
                        ${selectedOrder.payment_details.amount?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {orderToArchive && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-800 border-amber-500/30 max-w-md w-full">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                {orderToArchive.archived ? (
                  <ArchiveRestore className="w-8 h-8 text-amber-400" />
                ) : (
                  <Archive className="w-8 h-8 text-amber-400" />
                )}
                <h2 className="text-xl font-bold text-white">
                  {orderToArchive.archived ? 'Restore order?' : 'Archive this order?'}
                </h2>
              </div>
              <p className="text-gray-300 text-sm">
                {orderToArchive.archived
                  ? 'The order will return to your active list and be included in totals again.'
                  : 'The order will be hidden from the active list and excluded from totals. You can restore it later.'}
              </p>
              <div className="bg-gray-900 rounded p-3 text-sm space-y-1">
                <div className="text-gray-400">
                  Order: <code className="text-blue-400">{orderToArchive.id}</code>
                </div>
                <div className="text-gray-400">
                  Customer: <span className="text-white">{orderToArchive.customer_email}</span>
                </div>
                <div className="text-gray-400">
                  Total: <span className="text-white">${orderToArchive.total_amount?.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setOrderToArchive(null)}
                  disabled={archiving}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                  onClick={handleArchive}
                  disabled={archiving}
                  data-testid="confirm-archive-btn"
                >
                  {archiving ? 'Working...' : orderToArchive.archived ? 'Restore' : 'Archive'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal — double confirmation (button click + type-to-confirm) */}
      {orderToDelete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-800 border-red-500/40 max-w-md w-full">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <h2 className="text-xl font-bold text-white">Permanently delete order?</h2>
              </div>
              <p className="text-gray-300 text-sm">
                This <strong className="text-red-400">cannot be undone</strong>. The order, its
                payment record, and all associated data will be removed forever. If you just want to
                clean up the dashboard, consider archiving instead.
              </p>
              <div className="bg-gray-900 rounded p-3 text-sm space-y-1">
                <div className="text-gray-400">
                  Order: <code className="text-blue-400">{orderToDelete.id}</code>
                </div>
                <div className="text-gray-400">
                  Customer: <span className="text-white">{orderToDelete.customer_email}</span>
                </div>
                <div className="text-gray-400">
                  Total: <span className="text-white">${orderToDelete.total_amount?.toFixed(2)}</span>
                </div>
                <div className="text-gray-400">
                  Status: <span className="text-white">{orderToDelete.order_status}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Type <code className="text-red-400 font-bold">{DELETE_CONFIRM_TEXT}</code> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={DELETE_CONFIRM_TEXT}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-red-500 outline-none font-mono"
                  autoFocus
                  data-testid="delete-confirm-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setOrderToDelete(null);
                    setDeleteConfirmText('');
                  }}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleDelete}
                  disabled={deleteConfirmText !== DELETE_CONFIRM_TEXT || deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete Permanently'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Refund Mark Modal */}
      {orderToRefund && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-800 border-orange-500/40 max-w-md w-full">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <RotateCcw className="w-8 h-8 text-orange-400" />
                <h2 className="text-xl font-bold text-white">Mark refund status</h2>
              </div>

              <div className="bg-gray-900 rounded p-3 text-sm space-y-1">
                <div className="text-gray-400">
                  Order: <code className="text-blue-400">{orderToRefund.id}</code>
                </div>
                <div className="text-gray-400">
                  Customer: <span className="text-white">{orderToRefund.customer_email}</span>
                </div>
                <div className="text-gray-400">
                  Total: <span className="text-white">${orderToRefund.total_amount?.toFixed(2)}</span>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 text-xs text-blue-200 flex gap-2">
                <HelpCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  When you actually issue a refund in <strong>Stripe's dashboard</strong>, this status auto-updates to "Refunded" via webhook. Use this dialog when a customer <strong>requests</strong> a refund or to manually clear/override the flag.
                </span>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Status</label>
                <select
                  value={refundForm.status}
                  onChange={(e) => setRefundForm({ ...refundForm, status: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                  data-testid="refund-status-select"
                >
                  <option value="requested">Refund Requested</option>
                  <option value="partially_refunded">Partially Refunded</option>
                  <option value="refunded">Refunded</option>
                  <option value="none">Clear refund flag</option>
                </select>
              </div>

              {refundForm.status !== 'none' && refundForm.status !== 'requested' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Refund amount (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={refundForm.amount}
                    onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                    placeholder={`Up to $${orderToRefund.total_amount?.toFixed(2) || '0.00'}`}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                    data-testid="refund-amount-input"
                  />
                </div>
              )}

              {refundForm.status !== 'none' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Notes (optional)</label>
                  <textarea
                    rows={3}
                    value={refundForm.notes}
                    onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })}
                    placeholder="e.g. Customer reported missing items"
                    className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                  />
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setOrderToRefund(null)}
                  disabled={savingRefund}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={handleRefund}
                  disabled={savingRefund}
                  data-testid="confirm-refund-btn"
                >
                  {savingRefund ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tracking Modal */}
      {orderToTrack && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-800 border-sky-500/40 max-w-md w-full" data-testid="tracking-modal">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Truck className="w-8 h-8 text-sky-400" />
                <h2 className="text-xl font-bold text-white">Shipment tracking</h2>
              </div>

              <div className="bg-gray-900 rounded p-3 text-sm space-y-1">
                <div className="text-gray-400">
                  Order: <code className="text-blue-400">{orderToTrack.id}</code>
                </div>
                <div className="text-gray-400">
                  Customer: <span className="text-white">{orderToTrack.customer_email}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Carrier</label>
                <select
                  value={trackingForm.carrier}
                  onChange={(e) => setTrackingForm({ ...trackingForm, carrier: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                  data-testid="tracking-carrier-select"
                >
                  <option value="USPS">USPS</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                  <option value="DHL">DHL</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Tracking number</label>
                <input
                  type="text"
                  value={trackingForm.tracking_number}
                  onChange={(e) => setTrackingForm({ ...trackingForm, tracking_number: e.target.value })}
                  placeholder="e.g. 1Z999AA10123456784"
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white font-mono text-sm"
                  autoFocus
                  data-testid="tracking-number-input"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={trackingForm.notify_customer}
                  onChange={(e) => setTrackingForm({ ...trackingForm, notify_customer: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="tracking-notify-checkbox"
                />
                <span className="text-sm text-gray-300">
                  Mark as shipped & email the customer
                </span>
              </label>

              {!publicTrackingEnabled && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200 flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Public tracking is <strong>OFF</strong>. The email will be sent without a tracking link.
                    Turn it on in <code className="text-amber-300">/admin/settings</code> to include a live tracking page link.
                  </span>
                </div>
              )}

              {orderToTrack.tracking_token && publicTrackingEnabled && (
                <div className="bg-sky-500/10 border border-sky-500/30 rounded p-3 text-xs text-sky-200 flex items-center justify-between gap-2">
                  <span>Public tracking URL is live</span>
                  <button
                    onClick={() => copyTrackingLink(orderToTrack)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-500/20 hover:bg-sky-500/30"
                    data-testid="copy-tracking-url-btn"
                  >
                    <Copy className="w-3 h-3" /> Copy link
                  </button>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setOrderToTrack(null)}
                  disabled={savingTracking}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-sky-500 hover:bg-sky-600 text-white"
                  onClick={handleTrackingSave}
                  disabled={savingTracking}
                  data-testid="confirm-tracking-btn"
                >
                  {savingTracking ? 'Saving...' : 'Save tracking'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
